<ng-template #rightPanel>
  @if (isReseller || !hasBundles) {
    <div class="card card-animate mb-0 h-100 distribution-card">
      <div class="card-header border-0">
        <h5 class="card-title mb-1">Cost Breakdown by Zone</h5>
        <p class="text-muted mb-0 small">{{ isReseller ? 'Usage this month - please select a company to view bundle usage' : 'Pay As You Go usage this month' }}</p>
      </div>

      <div class="card-body">
        <div class="row g-3">
          @for (zone of zoneCosts; track $index) {
            <div [ngClass]="rightPanelMoved ? 'col-12' : 'col-md-6'">
              <div class="zone-card border rounded p-3 h-100">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <h6 class="mb-1">{{zone.title}}</h6>
                    <p class="text-muted small mb-0">{{zone.subtitle}}</p>
                  </div>
                  <h5 class="mb-0">{{zone.cost}}</h5>
                </div>
                <div class="d-flex justify-content-between mt-2 pt-2 border-top">
                  <div>
                    <p class="text-muted small mb-0">Usage</p>
                    <strong>{{zone.usage}}</strong>
                  </div>
                  <div class="text-end">
                    <p class="text-muted small mb-0">SIMs</p>
                    <strong>{{zone.sims}}</strong>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  } @else if (bundles.length===1) {
    <div class="card card-animate mb-0 h-100 distribution-card">
      <div class="card-header border-0">
        <h5 class="card-title mb-1">{{ bundles[0].zoneNames.length < 3 ? 'Data Bundle Usage' : 'Global Plan Usage' }}</h5>
        <p class="text-muted mb-0 small">{{ bundles[0].zoneNames.length < 3 ? 'Current billing cycle' : 'Single worldwide data bundle' }}</p>
      </div>

      <div class="card-body">
        <!-- Top global plan card -->
        <div class="global-plan-card border rounded p-3 mb-3">
          <div class="d-flex justify-content-between align-items-start mb-3">
            <div class="d-flex align-items-start">
              <div class="globe-icon me-2 d-flex align-items-center justify-content-center">
                <i class="bi bi-globe2 text-info"></i>
              </div>
              <div>
                <h6 class="mb-1">{{ bundles[0].bundleName }}</h6>
                <p class="text-muted small mb-0">
                  @if (bundles[0].systemZoneCount && bundles[0].zoneNames.length === bundles[0].systemZoneCount) {
                    All zones
                  } @else {
                    {{ bundles[0].zoneNames.length }} {{ bundles[0].zoneNames.length === 1 ? 'zone' : 'zones' }}
                  }
                  @if (bundles[0].countriesCount) { · {{bundles[0].countriesCount}} countries }
                  @if (bundles[0].avgMBPerSim) { · Avg {{bundles[0].avgMBPerSim | number:'1.0-0'}} MB/SIM }
                </p>
              </div>
            </div>
            <div class="text-end">
              <h4 class="mb-0" [ngClass]="bundleTextClass(bundles[0])">{{bundlePercentage(bundles[0])}}%</h4>
              <p class="text-muted text-uppercase small mb-0" style="font-size: 0.65rem;">Used</p>
            </div>
          </div>

          <div class="progress mb-3" style="height: 10px;">
            <div
              class="progress-bar"
              [ngClass]="bundleProgressClass(bundles[0])"
              role="progressbar"
              [style.width.%]="bundlePercentage(bundles[0])"
            ></div>
          </div>

          <div class="d-flex justify-content-between mb-3">
            <div>
              <p class="text-muted small mb-0">Used</p>
              <strong>{{ formatDataWeight(bundles[0].used, bundles[0].dataWeightCode) }}</strong>
            </div>
            <div class="text-center">
              <p class="text-muted small mb-0">Remaining</p>
              <strong class="text-success">{{ formatDataWeight(bundles[0].remaining, bundles[0].dataWeightCode) }}</strong>
            </div>
            <div class="text-end">
              <p class="text-muted small mb-0">Total Allowance</p>
              <strong>{{ formatDataWeight(bundles[0].totalAllowance, bundles[0].dataWeightCode) }}</strong>
            </div>
          </div>

          <div class="d-flex justify-content-between small text-muted pt-2 border-top">
            <span>{{bundleResetLabel(bundles[0])}} · {{bundleResetDateLabel(bundles[0])}}</span>
            <span>
              <span class="me-1">Coverage:</span>
              <strong class="text-dark">{{ bundles[0].zoneNames.join(', ') }}</strong>
            </span>
          </div>
        </div>

        <!-- Usage by zone -->
        @if (singleBundleZoneUsage.length>0) {
          <p class="text-muted text-uppercase small fw-semibold mb-2" style="font-size: 0.7rem;">Usage by Zone</p>
          <div class="row g-2 mb-3">
            @for (zone of singleBundleZoneUsage; track $index) {
              <div [ngClass]="rightPanelMoved ? 'col-12' : 'col-md-6'">
                <div class="zone-usage-row border rounded p-2">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <span class="small fw-medium">{{ zone.zone }}</span>
                    <strong class="small">{{ zone.totalUsage }}</strong>
                  </div>
                  <div class="progress" style="height: 4px;">
                    <div class="progress-bar"
                         role="progressbar"
                         [style.background-color]="zoneColour($index)"
                         [style.width.%]="zoneUsagePercent(zone)"></div>
                  </div>
                </div>
              </div>
            }
          </div>
        }

        <!-- Out-of-Bundle Usage (shared) -->
        <div class="border rounded p-3 out-of-bundle-section">
          <div class="d-flex align-items-center mb-2">
            <i class="bi bi-globe2 me-2 text-muted"></i>
            <div>
              <h6 class="mb-0">Out-of-Bundle Usage</h6>
              <p class="text-muted small mb-0">Usage in zones not covered by your global bundle</p>
            </div>
          </div>

          <div class="row g-2 mt-2">
            @for (zone of outOfBundleZones; track $index) {
              <div [ngClass]="rightPanelMoved ? 'col-12' : 'col-md-4'">
                <div class="zone-card border rounded p-2 h-100 d-flex flex-column">
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 class="mb-1 small">{{zone.zone}}</h6>
                      <p class="text-muted mb-0" style="font-size: 0.7rem;">{{zone.costPerMB | number:'1.4-4'}}/MB</p>
                    </div>
                    <strong class="small">{{zone.totalCostWithCurrencySymbol}}</strong>
                  </div>
                  <p class="text-muted mb-0 mt-auto pt-2 border-top" style="font-size: 0.7rem;">Usage: {{zone.totalUsage}}</p>
                </div>
              </div>
            }
            <div [ngClass]="rightPanelMoved ? 'col-12' : 'col-md-4'">
              <div class="zone-card border rounded p-2 h-100 d-flex flex-column">
                <p class="text-muted mb-0 text-uppercase" style="font-size: 0.65rem;">Total Out-of-Bundle</p>
                <h5 class="mb-1 mt-2">{{outOfBundleTotalCost}}</h5>
                <p class="text-muted mb-0 mt-auto pt-2 border-top" style="font-size: 0.7rem;">Total Usage: {{ formatDataWeight(outOfBundleTotalUsageGB, 'GB') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  } @else {
    <div class="card card-animate mb-0 h-100 distribution-card">
      <div class="card-header border-0">
        <h5 class="card-title mb-1">Data Bundle Usage</h5>
        <p class="text-muted mb-0 small">Current billing cycle</p>
      </div>

      <div class="card-body">
        <div class="row g-3 mb-3">
          @for (bundle of bundles; track $index) {
            <div [ngClass]="rightPanelMoved ? 'col-12' : 'col-md-6'">
              <div class="bundle-card border rounded p-3 h-100">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <h6 class="mb-1">
                      @if (bundle.bundleCode) {
                        <span class="bundle-flag-code text-muted me-2">{{bundle.bundleCode}}</span>
                      }
                      {{bundle.bundleName}}
                    </h6>
                    <p class="text-muted small mb-0">
                      {{ bundle.zoneNames.join(', ') }}
                      @if (bundle.countriesCount) { · {{bundle.countriesCount}} countries }
                      @if (bundle.avgMBPerSim) { · Avg {{bundle.avgMBPerSim | number:'1.0-0'}} MB/SIM }
                    </p>
                  </div>
                  <h5 class="mb-0" [ngClass]="bundleTextClass(bundle)">{{bundlePercentage(bundle)}}%</h5>
                </div>

                <div class="progress my-2" style="height: 6px;">
                  <div
                    class="progress-bar"
                    [ngClass]="bundleProgressClass(bundle)"
                    role="progressbar"
                    [style.width.%]="bundlePercentage(bundle)"
                  ></div>
                </div>

                <div class="d-flex justify-content-between small mt-2">
                  <div>
                    <p class="text-muted mb-0">Used</p>
                    <strong>{{ formatDataWeight(bundle.used, bundle.dataWeightCode) }}</strong>
                  </div>
                  <div class="text-center">
                    <p class="text-muted mb-0">Remaining</p>
                    <strong>{{ formatDataWeight(bundle.remaining, bundle.dataWeightCode) }}</strong>
                  </div>
                  <div class="text-end">
                    <p class="text-muted mb-0">Total Allowance</p>
                    <strong>{{ formatDataWeight(bundle.totalAllowance, bundle.dataWeightCode) }}</strong>
                  </div>
                </div>

                <div class="d-flex justify-content-between small text-muted mt-2 pt-2 border-top">
                  <span>{{bundleResetLabel(bundle)}}</span>
                  <span>{{bundleResetDateLabel(bundle)}}</span>
                </div>
              </div>
            </div>
          }
        </div>

        <div class="border rounded p-3 out-of-bundle-section">
          <div class="d-flex align-items-center mb-2">
            <i class="bi bi-globe2 me-2 text-muted"></i>
            <div>
              <h6 class="mb-0">Out-of-Bundle Usage</h6>
              <p class="text-muted small mb-0">Usage in zones not covered by your bundles</p>
            </div>
          </div>

          <div class="row g-2 mt-2">
            @for (zone of outOfBundleZones; track $index) {
              <div [ngClass]="rightPanelMoved ? 'col-12' : 'col-md-4'">
                <div class="zone-card border rounded p-2 h-100 d-flex flex-column">
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 class="mb-1 small">{{zone.zone}}</h6>
                      <p class="text-muted mb-0" style="font-size: 0.7rem;">{{zone.costPerMB | number:'1.4-4'}}/MB</p>
                    </div>
                    <strong class="small">{{zone.totalCostWithCurrencySymbol}}</strong>
                  </div>
                  <p class="text-muted mb-0 mt-auto pt-2 border-top" style="font-size: 0.7rem;">Usage: {{zone.totalUsage}}</p>
                </div>
              </div>
            }
            <div [ngClass]="rightPanelMoved ? 'col-12' : 'col-md-4'">
              <div class="zone-card border rounded p-2 h-100 d-flex flex-column">
                <p class="text-muted mb-0 text-uppercase" style="font-size: 0.65rem;">Total Out-of-Bundle</p>
                <h5 class="mb-1 mt-2">{{outOfBundleTotalCost}}</h5>
                <p class="text-muted mb-0 mt-auto pt-2 border-top" style="font-size: 0.7rem;">Total Usage: {{ formatDataWeight(outOfBundleTotalUsageGB, 'GB') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  }
</ng-template>

<ng-template #distributionContent>
  <div class="row g-3">

    <div [ngClass]="rightPanelMoved ? 'col-12' : 'col-xl-7 col-lg-12'">
      <div class="card card-animate mb-0 distribution-card">
        <div class="card-header border-0">
          <div class="d-flex justify-content-between align-items-start">
            <h5 class="card-title mb-1">Global SIM Distribution</h5>

            @if (mapRiskSourceID!==undefined) {
              <div class="btn-group distribution-mode-toggle" role="group" aria-label="Map view">
                <input
                  [checked]="mapMode==='vector'"
                  id="distributionMapViewStandard"
                  name="distributionMapView"
                  type="radio" class="btn-check"
                  value="vector"
                  (change)="setMapView('vector')"
                >
                <label class="btn btn-outline-success mb-0" for="distributionMapViewStandard">Standard</label>

                <input
                  [checked]="mapMode==='risk'"
                  class="btn-check"
                  id="distributionMapViewRisk"
                  name="distributionMapView"
                  type="radio"
                  value="risk"
                  (change)="setMapView('risk')"
                >
                <label class="btn btn-outline-success mb-0" for="distributionMapViewRisk">Risk</label>
              </div>
            }
          </div>
          @if (mapMode==='risk') {
            <p class="text-muted mb-0 small">{{riskMapSimsLocated | number}} of {{riskMapSimsTotal | number}} SIMs located across {{riskMapCountries}} {{riskMapCountries===1 ? 'country' : 'countries'}} · Current location</p>
          } @else if (hasUnknownLocation) {
            <p class="text-muted mb-0 small">{{activeSimsLocated | number}} of {{activeSimsTotal | number}} active SIMs seen across {{activeCountries}} {{activeCountries===1 ? 'country' : 'countries'}}</p>
          } @else {
            <p class="text-muted mb-0 small">Active SIMs across {{activeCountries}} {{activeCountries===1 ? 'country' : 'countries'}}</p>
          }
        </div>

        <div class="card-body d-flex flex-column">
          @if (mapMode==='pending') {
            <div class="distribution-risk-map-pending rounded"></div>
          } @else if (mapMode==='risk') {
            <div class="distribution-risk-map" [class.distribution-risk-map-wide]="rightPanelMoved">
              <div class="distribution-risk-map-inner">
                @if (mapCompanyId!==undefined) {
                  <app-map-sim-locations
                    [companyId]="mapCompanyId"
                    [filterListToCountrySelection]="true"
                    [fitWorldToWidth]="true"
                    [intendsRiskColouring]="true"
                    [keepKnownCountry]="true"
                    [openFullyZoomedOut]="true"
                    [riskDataSourceID]="mapRiskSourceID ?? eTravelRiskDataProvider.SureSIM"
                    [selectedUserZoomLevel]="16"
                    [showRiskColouring]="true"
                    [showRiskLegend]="true"
                    [sidebarCollapsible]="true"
                    [sidebarOpenByDefault]="false"
                    [wideSidebar]="true"
                    [worldZoomLevel]="2"
                    (countryHovered)="onRiskMapCountryHovered($event)"
                    (locationDataLoaded)="onRiskMapLocationDataLoaded($event)"
                    (sidebarCollapsedChange)="onRiskMapSidebarCollapsedChange($event)"
                  ></app-map-sim-locations>
                }

                <app-riskline-attribution
                  [alignEnd]="true"
                  [dataSourceID]="mapRiskSourceID ?? null"
                  [hasRisklineEquivalent]="true"
                  [hideDisclaimer]="!showRiskMapDisclaimer || mapRiskSourceID!==eTravelRiskDataProvider.Riskline"
                  (sourceChanged)="switchRiskMapSource($event)"
                ></app-riskline-attribution>
              </div>
            </div>
          } @else {
            <div class="position-relative">
              <div #mapContainer class="distribution-map rounded"></div>
  
              <div class="distribution-map-legend bg-white border rounded shadow-sm px-2 py-1">
                <div class="d-flex align-items-center small mb-1">
                  <span class="legend-dot me-2" style="background-color: #7269ef;"></span>
                  <span class="text-muted">SIMs Active</span>
                </div>
                <div class="d-flex align-items-center small">
                  <span class="legend-dot me-2" style="background-color: #e3e5ee;"></span>
                  <span class="text-muted">No SIMs</span>
                </div>
              </div>
  
              @if (tooltipVisible) {
                <div
                  class="region-tooltip"
                  [style.left.px]="tooltipX"
                  [style.top.px]="tooltipY"
                >
                  <div class="region-tooltip-title">{{ hoveredCountryDisplayName }}</div>
                  @if (hoveredCountry) {
                    <div><span class="region-tooltip-label">SIMs:</span> {{ (hoveredCountry.noOfICCIDs ?? 0) | number }}</div>
                  } @else {
                    <div class="text-muted-soft">No SIMs in this country</div>
                  }
                </div>
              }
            </div>
          }

          <div class="row g-2 mt-auto pt-3">
            @for (region of (mapMode==='risk' ? riskMapRegionStats : regionStats); track $index) {
              <div class="col">
                <div
                  class="region-tile text-center rounded border py-3"
                  [class.region-tile-hovered]="hoveredRegionLabel===region.label"
                >
                  <h4 class="mb-1">{{region.value | number}}</h4>
                  <p class="text-muted text-uppercase small mb-0">{{region.label}}</p>
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    </div>

    @if (!rightPanelMoved) {
      <div class="col-xl-5 col-lg-12">
        <ng-container *ngTemplateOutlet="rightPanel"></ng-container>
      </div>
    }

  </div>
</ng-template>

@if (useContainer) {
  @if (visibleContainer) {
    <div class="row" [ngClass]="marginBottom">
      <div class="col-12">
        <div class="border border-success rounded">
          <div class="row">
            <div class="col-12">
              <div class="alert border-0 alert-primary d-flex justify-content-between align-items-center mb-0"
                   role="alert">
                <strong>{{containerTitle}}</strong>
                <div class="d-flex align-items-center" style="gap: 6px;">
                  <i class="fs-5"
                     [ngClass]="expandContainer ? 'bx bx-up-arrow' : 'bx bx-down-arrow'"
                     style="cursor: pointer; color: #198754;"
                     (click)="toggleContainerExpansion()">
                  </i>
                  <i *ngIf="allowContainerClose"
                     class="fs-3 bx bx-x"
                     style="cursor: pointer; color: #dc3545;"
                     (click)="closeContainer()">
                  </i>
                </div>
              </div>
            </div>
          </div>

          @if (expandContainer) {
            <div class="row">
              <div class="col-12">
                <div class="mb-2 w-100 px-2 pt-2">
                  <ng-container *ngTemplateOutlet="distributionContent"></ng-container>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  }
} @else {
  <ng-container *ngTemplateOutlet="distributionContent"></ng-container>
}
